import routes from '../routes';

const getWindowOrigin = () => window.location.origin;

const escapeRegex = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const routePatternToRegex = route => {
  const segments = route.split('/').filter(Boolean);
  const pattern = segments
    .map(segment => {
      if (!segment.startsWith(':')) {
        return `/${escapeRegex(segment)}`;
      }

      return segment.endsWith('?') ? '(?:/[^/]+)?' : '/[^/]+';
    })
    .join('');

  return new RegExp(`^${pattern || '/'}\\/?$`);
};

const getFetchUrl = input => {
  if (typeof input === 'string') {
    return input;
  }

  if (input instanceof URL) {
    return input.href;
  }

  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.url;
  }

  if (input?.url) {
    return input.url;
  }

  return String(input || '');
};

const parseFetchUrl = input => {
  try {
    return new URL(getFetchUrl(input), window.location.href);
  } catch {
    return null;
  }
};

const normalizePath = path => {
  if (!path || path === '/') {
    return '/';
  }

  return path.replace(/\/+$/, '');
};

const pathMatchesPrefix = (path, prefix) => {
  const normalizedPath = normalizePath(path);
  const normalizedPrefix = normalizePath(prefix);

  return normalizedPath === normalizedPrefix || normalizedPath.startsWith(`${normalizedPrefix}/`);
};

const getBackendBaseUrl = () => new URL(API_CONFIG.backendBaseUrl, getWindowOrigin());

const stripPathPrefix = (path, prefix) => {
  const normalizedPath = normalizePath(path);
  const normalizedPrefix = normalizePath(prefix);

  if (normalizedPrefix === '/') {
    return normalizedPath;
  }

  if (normalizedPath === normalizedPrefix) {
    return '/';
  }

  if (normalizedPath.startsWith(`${normalizedPrefix}/`)) {
    return normalizedPath.slice(normalizedPrefix.length) || '/';
  }

  return normalizedPath;
};

const getApiPath = parsedUrl => {
  try {
    const backendBaseUrl = getBackendBaseUrl();
    if (parsedUrl.origin === backendBaseUrl.origin) {
      return stripPathPrefix(parsedUrl.pathname, backendBaseUrl.pathname);
    }
  } catch {
    // Invalid environment URLs are ignored so auth headers fail closed elsewhere.
  }

  return normalizePath(parsedUrl.pathname);
};

// Extract all protected routes from your routes.js
const getProtectedRoutes = () => {
  const extractRoutes = routeArray => {
    let allRoutes = [];
    routeArray.forEach(route => {
      if (route.route && route.protected) {
        allRoutes.push(route.route);
      }
      if (route.collapse) {
        allRoutes = allRoutes.concat(extractRoutes(route.collapse));
      }
    });
    return allRoutes;
  };
  return extractRoutes(routes);
};

// Check if current page requires authentication
export const isOnProtectedRoute = () => {
  const currentPath = window.location.pathname;
  const protectedRoutes = getProtectedRoutes();

  return protectedRoutes.some(route => {
    return routePatternToRegex(route).test(currentPath);
  });
};

// Configuration - authenticate only trusted API destinations.
export const API_CONFIG = {
  backendBaseUrl: process.env.REACT_APP_BACKEND_URL || '/',

  // Path prefixes that indicate backend API calls.
  apiPathPrefixes: [
    '/api',
    '/data-explorer',
    '/devices',
    '/graphql',
    '/logout',
    '/ocp-reports',
    '/permissions',
    '/schedule',
    '/upload',
    '/users',
  ],

  // Never authenticate these, even when they are on a trusted origin.
  neverAuthenticate: [
    '/refresh', // Handled separately
    '/login',
    '/api/public',
    '/api/health',
    '/authentication', // Login/register/frontend auth routes
  ],

  // Static resources to ignore completely
  staticResources: ['.js', '.css', '.png', '.jpg', '.jpeg', '.gif', '.svg', '.ico', '.woff', '.woff2', '.ttf', '.eot', '.map'],

  // When true: Only authenticate when on protected routes
  respectRouteProtection: false,

  // Compatibility fallback for older same-origin endpoints that do not use a known API prefix.
  allowSameOriginHeuristic: true,
};

const getTrustedApiOrigins = () => {
  const origins = new Set([getWindowOrigin()]);

  try {
    origins.add(getBackendBaseUrl().origin);
  } catch {
    // Invalid environment URLs are ignored so auth headers fail closed.
  }

  return origins;
};

const isTrustedApiOrigin = parsedUrl => getTrustedApiOrigins().has(parsedUrl.origin);

const getAuthDecision = input => {
  const parsedUrl = parseFetchUrl(input);

  if (!parsedUrl) {
    return {
      shouldAuth: false,
      reason: 'invalid-url',
      url: getFetchUrl(input),
    };
  }

  const path = getApiPath(parsedUrl);
  const lowerPath = path.toLowerCase();

  if (API_CONFIG.staticResources.some(ext => lowerPath.endsWith(ext))) {
    return {
      shouldAuth: false,
      reason: 'static-resource',
      url: parsedUrl.href,
      path,
    };
  }

  const neverAuthMatch = API_CONFIG.neverAuthenticate.find(pattern => pathMatchesPrefix(path, pattern));

  if (neverAuthMatch) {
    return {
      shouldAuth: false,
      reason: 'never-auth',
      match: neverAuthMatch,
      url: parsedUrl.href,
      path,
    };
  }

  if (!isTrustedApiOrigin(parsedUrl)) {
    return {
      shouldAuth: false,
      reason: 'untrusted-origin',
      origin: parsedUrl.origin,
      url: parsedUrl.href,
      path,
    };
  }

  if (API_CONFIG.respectRouteProtection && !isOnProtectedRoute()) {
    return {
      shouldAuth: false,
      reason: 'not-on-protected-route',
      url: parsedUrl.href,
      path,
    };
  }

  const apiPrefixMatch = API_CONFIG.apiPathPrefixes.find(prefix => pathMatchesPrefix(path, prefix));

  if (apiPrefixMatch) {
    return {
      shouldAuth: true,
      reason: 'api-prefix',
      match: apiPrefixMatch,
      url: parsedUrl.href,
      path,
    };
  }

  const sameOriginHeuristic = API_CONFIG.allowSameOriginHeuristic && isSameOriginApiCall(parsedUrl, path);

  return {
    shouldAuth: sameOriginHeuristic,
    reason: sameOriginHeuristic ? 'same-origin-heuristic' : 'not-api',
    url: parsedUrl.href,
    path,
  };
};

// Main function: Should this URL be authenticated?
export const shouldAuthenticate = input => getAuthDecision(input).shouldAuth;

// Enhanced shouldAuthenticate with debugging
export const shouldAuthenticate_debug = input => {
  const decision = getAuthDecision(input);

  console.log('Auth decision for:', decision.url);
  console.log('  - reason:', decision.reason);
  console.log('  - match:', decision.match || 'none');
  console.log('  - origin:', decision.origin || parseFetchUrl(input)?.origin || 'unknown');
  console.log('  - API path:', decision.path || 'unknown');
  console.log('  - will authenticate:', decision.shouldAuth);

  return decision.shouldAuth;
};

// Helper: Detect same-origin API calls (smart heuristic)
const isSameOriginApiCall = (parsedUrl, apiPath = getApiPath(parsedUrl)) => {
  if (parsedUrl.origin !== getWindowOrigin()) {
    return false;
  }

  // Skip if it's just a page route
  const protectedRoutes = getProtectedRoutes();
  const isPageRoute = protectedRoutes.some(route => {
    return routePatternToRegex(route).test(parsedUrl.pathname);
  });

  if (isPageRoute) {
    return false;
  }

  // Older same-origin endpoints sometimes do not use one of the known API prefixes.
  const pathSegments = apiPath.split('/').filter(Boolean);
  return pathSegments.length >= 2; // e.g., /something/else
};

// Helper for refresh detection
export const isRefreshRequest = input => {
  const parsedUrl = parseFetchUrl(input);
  return Boolean(parsedUrl && pathMatchesPrefix(getApiPath(parsedUrl), '/refresh'));
};

// Debug helpers
export const debugRouteInfo = () => {
  console.group('🔍 Route-Based Auth Debug');
  console.log('Current path:', window.location.pathname);
  console.log('Is on protected route:', isOnProtectedRoute());
  console.log('Protected routes:', getProtectedRoutes());
  console.groupEnd();
};

// Test a URL to see if it would be authenticated
export const testUrl = url => {
  const decision = getAuthDecision(url);
  const result = {
    url,
    onProtectedRoute: isOnProtectedRoute(),
    shouldAuth: decision.shouldAuth,
    reason: decision.reason,
    match: decision.match || null,
    isRefresh: isRefreshRequest(url),
  };
  console.table(result);
  return result;
};
